import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Mail, CreditCard, CheckCircle2, AlertCircle, Send } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Header from '../../components/navigation/Header';
import { supabase } from '../../lib/supabase';


export default function PaymentContact() {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Prepare email content
      const emailHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background: #16a34a; color: white; padding: 20px; text-align: center; }
            .content { padding: 20px; background: #f9fafb; }
            .info-section { background: white; border-left: 4px solid #16a34a; padding: 15px; margin: 20px 0; }
            .footer { text-align: center; padding: 20px; color: #6b7280; font-size: 12px; }
            .label { font-weight: bold; color: #374151; }
            .value { color: #1f2937; margin-bottom: 10px; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>📧 New Invoice Request</h1>
            </div>
            <div class="content">
              <p>A new invoice request has been submitted through the payment contact form.</p>
              
              <div class="info-section">
                <h3>Contact Information</h3>
                <div class="value"><span class="label">Company Name:</span> ${formData?.companyName}</div>
                <div class="value"><span class="label">Contact Name:</span> ${formData?.contactName}</div>
                <div class="value"><span class="label">Email:</span> ${formData?.email}</div>
                <div class="value"><span class="label">Phone:</span> ${formData?.phone || 'Not provided'}</div>
              </div>

              ${formData?.message ? `
              <div class="info-section">
                <h3>Additional Information</h3>
                <p>${formData?.message}</p>
              </div>
              ` : ''}

              <p><strong>Action Required:</strong></p>
              <p>Please prepare and send an invoice to <strong>${formData?.email}</strong> within 24 hours.</p>
            </div>
            <div class="footer">
              <p>This is an automated message from PNG HR Management System</p>
              <p>Pacific RBS Ltd | admin@hrpacificrbs.com | +675 78808282</p>
            </div>
          </div>
        </body>
        </html>
      `;

      // Send email via Supabase Edge Function
      const { data, error: emailError } = await supabase?.functions?.invoke('send-compliance-email', {
        body: {
          to: 'admin@hrpacificrbs.com',
          subject: `Invoice Request from ${formData?.companyName} - ${formData?.contactName}`,
          html: emailHtml,
          type: 'invoice_request',
          metadata: {
            companyName: formData?.companyName,
            contactName: formData?.contactName,
            email: formData?.email,
            phone: formData?.phone,
            timestamp: new Date()?.toISOString()
          }
        }
      });

      if (emailError) {
        throw new Error(emailError?.message || 'Failed to send email');
      }
      
      setSubmitted(true);
      setFormData({
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (err) {
      console.error('Error sending invoice request:', err);
      setError(err?.message || 'Failed to send request. Please try again or contact us directly at admin@hrpacificrbs.com');
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Helmet>
        <title>Payment & Invoice Request - PNG HR Management</title>
        <meta name="description" content="Contact us for payment information and invoice requests" />
      </Helmet>
      <Header />
      <div className="min-h-screen bg-background py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full mb-4">
              <CreditCard className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-3">
              Payment & Invoice Request
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Contact us to receive an invoice for your subscription. We'll send you payment details and instructions via email.
            </p>
          </div>

          {/* Success Message */}
          {submitted && (
            <div className="mb-8 p-6 bg-success/10 border border-success/20 rounded-lg">
              <div className="flex items-start gap-4">
                <CheckCircle2 className="h-6 w-6 text-success flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-success text-lg mb-2">Request Sent Successfully!</h3>
                  <p className="text-sm text-success/90">
                    Thank you for your interest. We've received your invoice request and will send payment details to your email within 24 hours.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-8 p-6 bg-destructive/10 border border-destructive/20 rounded-lg">
              <div className="flex items-start gap-4">
                <AlertCircle className="h-6 w-6 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-destructive text-lg mb-2">Error</h3>
                  <p className="text-sm text-destructive/90">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Contact Form */}
          <div className="bg-card border border-border rounded-lg p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <Mail className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-semibold text-foreground">Request Invoice via Email</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Company Name"
                  name="companyName"
                  value={formData?.companyName}
                  onChange={handleInputChange}
                  required
                  placeholder="Your company name"
                  description="Legal entity name for invoicing"
                />
                <Input
                  label="Contact Name"
                  name="contactName"
                  value={formData?.contactName}
                  onChange={handleInputChange}
                  required
                  placeholder="Your full name"
                  description="Primary contact person"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData?.email}
                  onChange={handleInputChange}
                  required
                  placeholder="your.email@company.com"
                  description="Where we'll send the invoice"
                />
                <Input
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  value={formData?.phone}
                  onChange={handleInputChange}
                  placeholder="+675 XXX XXXX"
                  description="For urgent inquiries (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Additional Information
                </label>
                <textarea
                  name="message"
                  value={formData?.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Any specific requirements or questions about payment..."
                />
                <p className="text-xs text-muted-foreground mt-1">Optional: Include subscription tier, billing preferences, or questions</p>
              </div>

              {/* Payment Information */}
              <div className="bg-muted/30 border border-border rounded-lg p-6">
                <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  What Happens Next?
                </h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>We'll review your request and prepare a customized invoice</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Invoice with payment instructions will be sent to your email within 24 hours</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Payment methods include bank transfer, cheque, or online payment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Your subscription will be activated upon payment confirmation</span>
                  </li>
                </ul>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => window.history?.back()}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={loading}
                  disabled={loading}
                  className="flex items-center gap-2"
                >
                  <Send size={16} />
                  {loading ? 'Sending...' : 'Request Invoice'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}